/**
 * Custom hook untuk mengambil semua data agent
 * 
 * API Endpoint: GET `/api/agent`
 * API Version: 3.0
 * 
 * @param {string} id - ID agent
 * @param {string} name - Nama agent
 * @param {string} email - Email agent
 * @param {string} phone - Nomor telepon agent
 * @returns {object} - Semua data booking yang telah diformat
 * @example
 * const { data, isLoading, error } = useGetAllBooking();
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { AgentResponseSchema } from "../response/IndexAgentResponse";

const API_VERSION = "v3";

interface FilterProps {
    all?: number;
    id?: string;
    property_id?: string;
    name?: string;
    email?: string;
    phone?: string;
}

interface IndexAgentProps {
    filters?: FilterProps,
    search?: string,
    limit?: number,
    queryKey?: string,
    page?: number,
}

const useIndexAgent = (query: IndexAgentProps) => {
    return useQuery({
        queryKey: [
            'agents',
            query.filters?.all ? 'all' : '',
            ...Object.entries(query.filters ?? {})
                .filter(([key, value]) => key !== 'all' && value !== undefined && value !== null && value !== "")
                .map(([key, value]) => `${key}:${value}`),
            query.search,
            query.queryKey,
            query.page,
        ],
        queryFn: async () => {
            try {
                const filter = Object.fromEntries(
                    Object.entries(query.filters ?? {}).filter(([key, value]) => key !== "all" && value !== undefined && value !== null && value !== "")
                );
                const params = query.filters?.all
                    ? { all: 1, filter }
                    : {
                        filter,
                        search: query.search || "",
                        limit: query.limit ?? 10,
                        page: query.page,
                    };

                const response = await privateApi.get(`/${API_VERSION}/agent`, { params });

                // Validasi response
                const validationResult = AgentResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Agent data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Agent`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useIndexAgent;